import React, { useEffect, useMemo, useRef, useState } from "react";
import { useMutation } from "@apollo/client";
import { UPDATE_VENUE } from "./Venue";
import { Autocomplete, useLoadScript } from "@react-google-maps/api";
import Loading from "../common/Loading";
import { Form } from "react-router-dom";
import { toast } from "react-toastify";
import { GlobalStyles } from "../../constants/styles";
import useThemeColors from "../organization/portal/useThemeColors";
import { alphaFromHex } from "../../util/colors";
import useTranslation from "../../i18n/useTranslation";
import { getLocalizedGraphQLError } from "../../util/graphqlError";

export default function VenueEditModal({ show, onClose, venue, refetch }) {
    const scopeId = "kbola-venue-edit-modal";
    const colors = useThemeColors();
    const { t } = useTranslation();

    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [searchResult, setSearchResult] = useState(null);
    const fieldAddressInput = useRef(null);
    const [uploading, setUploading] = useState(false);

    const initialLocation = {
        address: "",
        city: "",
        state: "",
        country: "",
        latitude: "",
        longitude: "",
        placeId: "",
    };

    const [location, setLocation] = useState(initialLocation);

    /**
     * IMPORTANT:
     * Existing fields must keep their id.
     * New fields use id: null.
     * Removed fields are simply removed from this state, so the backend can delete only those missing ids.
     */
    const [fields, setFields] = useState([{ id: null, name: "", description: "" }]);

    useEffect(() => {
        if (!show) return;

        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        return () => {
            document.body.style.overflow = prev || "";
        };
    }, [show]);

    const ui = useMemo(() => {
        return {
            backdrop: {
                backgroundColor: "rgba(0,0,0,0.55)",
            },
            dialog: {
                maxWidth: 820,
            },
            content: {
                borderRadius: 18,
                overflow: "hidden",
                border: "1px solid rgba(0,0,0,0.08)",
                boxShadow: "0 18px 44px rgba(0,0,0,0.18)",
            },

            header: {
                padding: "12px 14px",
                background: `linear-gradient(
                    135deg,
                    ${alphaFromHex(colors.primary, 0.5)},
                    ${alphaFromHex(colors.primary, 0)}
                )`,
                borderBottom: "1px solid rgba(0,0,0,0.06)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                position: "sticky",
                top: 0,
                zIndex: 3,
            },
            footer: {
                padding: "12px 14px",
                borderTop: "1px solid rgba(0,0,0,0.06)",
                background: "rgba(255,255,255,0.96)",
                backdropFilter: "blur(2px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                position: "sticky",
                bottom: 0,
                zIndex: 3,
            },

            titleWrap: {
                display: "flex",
                alignItems: "center",
                gap: 10,
                minWidth: 0,
            },
            badge: {
                fontSize: 11.5,
                fontWeight: 900,
                letterSpacing: "0.03em",
                padding: "6px 10px",
                borderRadius: 999,
                background: colors.primary,
                border: "1px solid rgba(0,0,0,0.10)",
                color: colors.textOnPrimary,
                whiteSpace: "nowrap",
            },
            title: {
                margin: 0,
                fontWeight: 900,
                fontSize: 14,
                color: "#14151a",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
            },
            sub: {
                margin: 0,
                fontSize: 12.5,
                fontWeight: 700,
                color: "rgba(20,21,26,0.55)",
            },
            closeBtn: {
                width: 38,
                height: 38,
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.10)",
                background: "#fff",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                lineHeight: 1,
            },

            body: {
                padding: 14,
                background: "#fff",
            },

            sectionCard: {
                borderRadius: 14,
                padding: 12,
                border: "1px solid rgba(0,0,0,0.06)",
                background: "rgba(0,0,0,0.015)",
            },
            sectionTitle: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                marginBottom: 10,
            },
            sectionTitleText: {
                margin: 0,
                fontWeight: 1000,
                fontSize: 13,
                color: "rgba(20,21,26,0.85)",
            },
            fieldMeta: {
                fontWeight: 900,
                fontSize: 12,
                color: "rgba(20,21,26,0.55)",
            },

            label: {
                fontWeight: 900,
                fontSize: 12.5,
                color: "rgba(20,21,26,0.72)",
                marginBottom: 6,
            },
            input: {
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.10)",
                fontWeight: 800,
            },
            textarea: {
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.10)",
                fontWeight: 800,
            },

            addFieldBtn: {
                borderRadius: 12,
                padding: "10px 12px",
                fontWeight: 900,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                border: "1px solid rgba(0,0,0,0.12)",
                background: colors.secondary,
                color: colors.textOnSecondary,
                cursor: "pointer",
            },
            btnGhost: {
                borderRadius: 12,
                padding: "10px 12px",
                fontWeight: 900,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                border: "1px solid rgba(0,0,0,0.12)",
                background: "#fff",
                color: "#14151a",
                cursor: "pointer",
            },
            btnDangerGhost: {
                borderRadius: 12,
                padding: "9px 11px",
                fontWeight: 900,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                border: "1px solid rgba(180,35,24,0.25)",
                background: "#fff",
                color: "#b42318",
                cursor: "pointer",
            },
            btnPrimary: {
                borderRadius: 12,
                padding: "10px 12px",
                fontWeight: 900,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                border: "1px solid rgba(0,0,0,0.10)",
                background: GlobalStyles.colors.canary,
                color: GlobalStyles.colors.woodsmoke,
                cursor: "pointer",
            },

            fieldRow: {
                borderRadius: 14,
                padding: 12,
                border: "1px solid rgba(0,0,0,0.06)",
                background: "#fff",
            },
            fieldRowHeader: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                marginBottom: 10,
            },
            fieldIndexBadge: {
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                fontSize: 12,
                fontWeight: 1000,
                color: "rgba(20,21,26,0.7)",
            },
            smallPill: {
                borderRadius: 999,
                padding: "4px 8px",
                background: "rgba(0,0,0,0.04)",
                border: "1px solid rgba(0,0,0,0.06)",
                fontSize: 11,
                fontWeight: 900,
                color: "rgba(20,21,26,0.55)",
            },
        };
    }, [colors.primary, colors.secondary, colors.textOnPrimary, colors.textOnSecondary]);

    const [updateVenue, { error }] = useMutation(UPDATE_VENUE);

    const { isLoaded } = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY,
        libraries: ["places"],
    });

    useEffect(() => {
        if (!venue) return;

        setName(venue.name || "");
        setDescription(venue.description || "");

        const nextLocation = {
            address: venue.address || "",
            city: venue.city || "",
            state: venue.state || "",
            country: venue.country || "",
            latitude: venue.latitude || "",
            longitude: venue.longitude || "",
            placeId: venue.placeId || "",
        };

        setLocation(nextLocation);

        setFields(
            venue.fields?.length
                ? venue.fields.map((f) => ({
                    id: f.id ? Number(f.id) : null,
                    name: f.name || "",
                    description: f.description || "",
                }))
                : [{ id: null, name: "", description: "" }]
        );

        if (fieldAddressInput.current) {
            fieldAddressInput.current.value = venue.address || "";
        }
    }, [venue]);

    const handleFieldChange = (index, key, value) => {
        setFields((prev) => {
            const next = [...prev];
            next[index] = {
                ...next[index],
                [key]: value,
            };
            return next;
        });
    };

    const handleAddField = (e) => {
        e.preventDefault();

        setFields((prev) => [
            ...prev,
            {
                id: null,
                name: "",
                description: "",
            },
        ]);
    };

    const handleRemoveField = (index) => {
        setFields((prev) => {
            if (prev.length <= 1) {
                return [{ id: null, name: "", description: "" }];
            }

            return prev.filter((_, i) => i !== index);
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setUploading(true);

        if (!name.trim()) {
            toast.error(t("venueEditModal.venueNameRequired"));
            setUploading(false);
            return;
        }

        if (!location.address) {
            toast.error(t("venueEditModal.addressRequired") || "Address is required.");
            setUploading(false);
            return;
        }

        const cleanedFields = fields
            .map((f) => ({
                id: f.id ? Number(f.id) : null,
                name: f.name?.trim() || "",
                description: f.description?.trim() || "",
            }))
            .filter((f) => f.name !== "");

        const input = {
            name: name.trim(),
            description: description?.trim() || "",
            location: {
                address: location.address || "",
                city: location.city || "",
                state: location.state || "",
                country: location.country || "",
                latitude: parseFloat(location.latitude) || null,
                longitude: parseFloat(location.longitude) || null,
                placeId: location.placeId || "",
            },
            fields: cleanedFields,
        };

        try {
            const { data } = await updateVenue({
                variables: {
                    id: Number(venue.id),
                    input,
                },
            });

            if (data?.updateVenue?.venue) {
                toast.success(t("venueEditModal.updateSuccess"));
                await refetch?.();
                onClose?.();
            }
        } catch (err) {
            toast.error(getLocalizedGraphQLError(err, t, "venueEditModal.updateFailed"));
        } finally {
            setUploading(false);
        }
    };

    const onLoad = (autocomplete) => {
        setSearchResult(autocomplete);
    };

    const onPlaceChanged = () => {
        if (!searchResult) return;

        const place = searchResult.getPlace();

        const locationObj = {
            address: place.formatted_address || "",
            city: "",
            state: "",
            country: "",
            latitude: place.geometry?.location?.lat?.() ?? "",
            longitude: place.geometry?.location?.lng?.() ?? "",
            placeId: place.place_id || "",
        };

        (place.address_components || []).forEach((component) => {
            const types = component.types || [];

            if (types.includes("locality")) {
                locationObj.city = component.long_name;
            }

            if (types.includes("administrative_area_level_1")) {
                locationObj.state = component.long_name;
            }

            if (types.includes("country")) {
                locationObj.country = component.long_name;
            }
        });

        setLocation(locationObj);
    };

    if (!isLoaded) return <Loading />;

    return (
        <div
            id={scopeId}
            className={`modal fade ${show ? "show d-block" : ""}`}
            tabIndex="-1"
            role="dialog"
            aria-hidden={!show}
            style={ui.backdrop}
        >
            <div className="modal-dialog modal-lg modal-dialog-scrollable" role="document" style={ui.dialog}>
                <div className="modal-content" style={ui.content}>
                    <Form method="post" onSubmit={handleSubmit}>
                        <div style={ui.header}>
                            <div style={ui.titleWrap}>
                                <span style={ui.badge}>{t("venueEditModal.badge")}</span>

                                <div style={{ minWidth: 0 }}>
                                    <h5 style={ui.title}>{t("venueEditModal.title")}</h5>
                                    <p style={ui.sub}>{t("venueEditModal.subtitle")}</p>
                                </div>
                            </div>

                            <button
                                type="button"
                                style={{
                                    ...ui.closeBtn,
                                    opacity: uploading ? 0.7 : 1,
                                }}
                                onClick={onClose}
                                title={t("venueEditModal.close")}
                                aria-label={t("venueEditModal.close")}
                                disabled={uploading}
                            >
                                <i className="fas fa-times" />
                            </button>
                        </div>

                        <div className="modal-body" style={ui.body}>
                            {error ? <div className="alert alert-danger">{t("venueEditModal.updateFailed")}</div> : null}

                            <div className="form-group">
                                <label style={ui.label}>{t("venueEditModal.venueName")}</label>
                                <input
                                    className="form-control"
                                    style={ui.input}
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    required
                                    disabled={uploading}
                                />
                            </div>

                            <div className="form-group">
                                <label style={ui.label}>{t("venueEditModal.description")}</label>
                                <textarea
                                    className="form-control"
                                    style={ui.textarea}
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    rows={3}
                                    disabled={uploading}
                                />
                            </div>

                            <div className="form-group">
                                <label style={ui.label}>{t("venueEditModal.address")}</label>

                                <Autocomplete onPlaceChanged={onPlaceChanged} onLoad={onLoad}>
                                    <input
                                        required
                                        className="form-control"
                                        style={ui.input}
                                        type="text"
                                        placeholder={t("venueEditModal.addressPlaceholder")}
                                        ref={fieldAddressInput}
                                        disabled={uploading}
                                    />
                                </Autocomplete>

                                {location?.address ? (
                                    <div className="mt-2" style={ui.sectionCard}>
                                        <div style={ui.fieldMeta}>
                                            <i className="fas fa-map-marker-alt mr-2" />
                                            {location.address}
                                        </div>

                                        <div style={{ ...ui.fieldMeta, marginTop: 6 }}>
                                            {location.city ? `${location.city}, ` : ""}
                                            {location.state ? `${location.state}, ` : ""}
                                            {location.country || ""}
                                        </div>
                                    </div>
                                ) : null}
                            </div>

                            <div className="mt-3" style={ui.sectionCard}>
                                <div style={ui.sectionTitle}>
                                    <h6 style={ui.sectionTitleText}>{t("venueEditModal.fieldsTitle")}</h6>

                                    <span style={{ ...ui.fieldMeta, margin: 0 }}>
                                        {t("venueEditModal.itemsCount", { count: fields.length })}
                                    </span>
                                </div>

                                {fields.map((field, index) => (
                                    <div key={field.id || `new-field-${index}`} style={{ ...ui.fieldRow, marginBottom: 10 }}>
                                        <div style={ui.fieldRowHeader}>
                                            <div style={ui.fieldIndexBadge}>
                                                <i className="fas fa-map-marked-alt" />
                                                <span>
                                                    {t("venueEditModal.fieldLabel", {
                                                        number: index + 1,
                                                    }) || `Field ${index + 1}`}
                                                </span>

                                                {field.id ? (
                                                    <span style={ui.smallPill}>
                                                        {t("venueEditModal.existingField") || "Existing"}
                                                    </span>
                                                ) : (
                                                    <span style={ui.smallPill}>
                                                        {t("venueEditModal.newField") || "New"}
                                                    </span>
                                                )}
                                            </div>

                                            <button
                                                type="button"
                                                style={{
                                                    ...ui.btnDangerGhost,
                                                    opacity: uploading ? 0.7 : 1,
                                                }}
                                                onClick={() => handleRemoveField(index)}
                                                disabled={uploading}
                                                title={t("venueEditModal.removeField") || "Remove field"}
                                            >
                                                <i className="fas fa-trash" />
                                                <span className="d-none d-sm-inline">
                                                    {t("venueEditModal.removeField") || "Remove"}
                                                </span>
                                            </button>
                                        </div>

                                        <div className="row">
                                            <div className="col-md-5">
                                                <div className="form-group mb-2">
                                                    <label style={ui.label}>{t("venueEditModal.fieldName")}</label>

                                                    <input
                                                        required={index === 0}
                                                        className="form-control"
                                                        style={ui.input}
                                                        placeholder={t("venueEditModal.fieldNamePlaceholder")}
                                                        value={field.name}
                                                        onChange={(e) =>
                                                            handleFieldChange(index, "name", e.target.value)
                                                        }
                                                        disabled={uploading}
                                                    />
                                                </div>
                                            </div>

                                            <div className="col-md-7">
                                                <div className="form-group mb-2">
                                                    <label style={ui.label}>
                                                        {t("venueEditModal.fieldDescription")}
                                                    </label>

                                                    <input
                                                        className="form-control"
                                                        style={ui.input}
                                                        placeholder={t("venueEditModal.fieldDescriptionPlaceholder")}
                                                        value={field.description}
                                                        onChange={(e) =>
                                                            handleFieldChange(index, "description", e.target.value)
                                                        }
                                                        disabled={uploading}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}

                                <button
                                    type="button"
                                    style={{
                                        ...ui.addFieldBtn,
                                        opacity: uploading ? 0.7 : 1,
                                    }}
                                    onClick={handleAddField}
                                    disabled={uploading}
                                >
                                    <i className="fas fa-plus" />
                                    {t("venueEditModal.addAnotherField")}
                                </button>
                            </div>
                        </div>

                        <div className="modal-footer" style={ui.footer}>
                            <button type="button" style={ui.btnGhost} onClick={onClose} disabled={uploading}>
                                {t("venueEditModal.cancel")}
                            </button>

                            <button
                                type="submit"
                                style={{
                                    ...ui.btnPrimary,
                                    opacity: uploading ? 0.8 : 1,
                                }}
                                disabled={uploading}
                            >
                                {uploading ? (
                                    <>
                                        <span
                                            className="spinner-border spinner-border-sm"
                                            role="status"
                                            aria-hidden="true"
                                        />
                                        {t("venueEditModal.saving")}
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-save" />
                                        {t("venueEditModal.updateVenue")}
                                    </>
                                )}
                            </button>
                        </div>
                    </Form>
                </div>
            </div>

            <style>{`
                #${scopeId} {
                    font-family: 'Poppins', sans-serif;
                }

                #${scopeId} .modal-dialog {
                    margin: 1.25rem auto;
                }

                #${scopeId} .modal-body {
                    max-height: calc(100vh - 220px);
                    overflow-y: auto;
                    -webkit-overflow-scrolling: touch;
                }

                #${scopeId} .form-control:focus,
                #${scopeId} button:focus {
                    outline: none;
                    box-shadow: 0 0 0 0.2rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.18);
                    border-color: rgba(var(--kbs-primary-rgb, 0,173,255), 0.65);
                }

                #${scopeId} button:disabled {
                    cursor: not-allowed;
                }

                #${scopeId} i.fas,
                #${scopeId} i.far,
                #${scopeId} i.fal,
                #${scopeId} i.fa,
                #${scopeId} i[class^="fa-"],
                #${scopeId} i[class*=" fa-"] {
                    font-family: "Font Awesome 5 Free" !important;
                    font-weight: 900;
                }

                #${scopeId} i.fab {
                    font-family: "Font Awesome 5 Brands" !important;
                    font-weight: 400;
                }
            `}</style>
        </div>
    );
}
